import { Language } from '../types';
import { generateExtendedCities } from './extendedCitiesData';

export interface LocalFaq {
  question: Record<Language, string>;
  answer: Record<Language, string>;
}

export interface CityData {
  slug: string;
  name: string;
  postalCode: string;
  region: 'Brussels' | 'Flanders' | 'Wallonia';
  regionLabel: Record<Language, string>;
  responseTime: string;
  landmarks: Record<Language, string[]>;
  headline: Record<Language, string>;
  description: Record<Language, string>;
  localIntro: Record<Language, string>;
  specialties: Record<Language, string[]>;
  faqs: LocalFaq[];
  testimonial: {
    author: string;
    neighborhood: string;
    text: Record<Language, string>;
    rating: number;
    service: Record<Language, string>;
  };
}

export const CORE_CITIES_DATA: CityData[] = [
  {
    slug: 'bruxelles',
    name: 'Bruxelles (1000)',
    postalCode: '1000',
    region: 'Brussels',
    regionLabel: {
      fr: 'Région de Bruxelles-Capitale',
      nl: 'Brussels Hoofdstedelijk Gewest',
      en: 'Brussels-Capital Region'
    },
    responseTime: '20 - 30 min',
    landmarks: {
      fr: ['Pentagone', 'Quartier Européen', 'Louise', 'Sablon', 'Laeken', 'Marolles'],
      nl: ['Vijfhoek', 'Europese Wijk', 'Louizawijk', 'Zavel', 'Laken', 'Marollen'],
      en: ['Pentagon', 'European Quarter', 'Louise District', 'Sablon', 'Laeken', 'Marolles']
    },
    headline: {
      fr: 'Plombier, Chauffagiste & Débouchage 24/7 à Bruxelles (1000)',
      nl: 'Loodgieter, Verwarmingsmonteur & Ontstoppingsdienst 24/7 in Brussel (1000)',
      en: 'Emergency Plumber, Heating & Drain Unblocking 24/7 in Brussels (1000)'
    },
    description: {
      fr: 'Dépannage d\'urgence 24h/24 et 7j/7 dans le centre de Bruxelles (1000, 1020, 1120, 1130). Recherche de fuite d\'eau non destructive, dépannage chaudière gaz et débouchage canalisations haute pression.',
      nl: 'Dringende hersteldienst 24/7 in centrum Brussel. Snelle lekdetectie zonder breekwerk, herstelling van cv-ketels op gas en hogedruk ontstopping van afvoerleidingen.',
      en: '24/7 emergency dispatch in downtown Brussels. Non-destructive leak detection, gas boiler repair, and high-pressure drain jetting in 30 minutes.'
    },
    localIntro: {
      fr: 'Nos techniciens circulent en permanence dans Bruxelles-Ville (Pentagone, Sablon, Quartier Européen, Laeken et Neder-Over-Heembeek) avec un équipement complet pour résoudre immédiatement vos pannes d\'eau chaude, fuites ou canalisations engorgées.',
      nl: 'Onze technici zijn permanent onderweg in Brussel-Stad (Vijfhoek, Zavel, Europese Wijk, Laken en Neder-Over-Heembeek) om uw sanitair en verwarmingsproblemen direct ter plaatse op te lossen.',
      en: 'Our certified mobile technicians patrol central Brussels (Pentagon, Sablon, European Quarter, Laeken) equipped to fix hot water breakdowns, pipe bursts, and clogged drains immediately.'
    },
    specialties: {
      fr: [
        'Dépannage plomberie express en 30 min dans le Pentagone',
        'Entretien légal et dépannage chaudière gaz agréé Bruxelles Environnement',
        'Hydrocurage et caméra thermique pour immeubles anciens et copropriétés',
        'Remplacement chauffe-eau et adoucisseur d\'eau anti-calcaire'
      ],
      nl: [
        'Spoedservice loodgieter binnen 30 min in de Vijfhoek',
        'Wettelijk ketelonderhoud door erkend technicus Leefmilieu Brussel',
        'Hogedrukreiniging en camera-inspectie voor herenhuizen en appartementsgebouwen',
        'Vervanging boilers en kalkwerende waterontharders'
      ],
      en: [
        'Express plumbing repair within 30 min in central Brussels',
        'Official gas boiler inspection and repair certified by Brussels Environment',
        'Hydro-jetting and sewer camera inspection for classic townhouses and buildings',
        'Water heater replacement and high-efficiency water softeners'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'En combien de temps intervenez-vous à Bruxelles centre ?',
          nl: 'Hoe snel zijn jullie ter plaatse in Brussel centrum?',
          en: 'How fast can you arrive in central Brussels?'
        },
        answer: {
          fr: 'Grâce à notre géolocalisation et nos véhicules d\'intervention basés à proximité, nous arrivons généralement en 20 à 30 minutes à Bruxelles (1000, 1020, 1120, 1130).',
          nl: 'Dankzij onze mobiele interventiewagens zijn we gemiddeld binnen 20 tot 30 minuten ter plaatse in Brussel (1000, 1020, 1120, 1130).',
          en: 'Thanks to our localized service fleet, our emergency technicians typically arrive within 20 to 30 minutes anywhere in Brussels.'
        }
      },
      {
        question: {
          fr: 'Délivrez-vous l\'attestation légale pour les chaudières à Bruxelles ?',
          nl: 'Leveren jullie het wettelijk attest af voor gasketels in Brussel?',
          en: 'Do you issue the legal certificate for gas boilers in Brussels?'
        },
        answer: {
          fr: 'Oui, nos chauffagistes sont habilités G1/G2 et fournissent l\'attestation de contrôle périodique officielle exigée par la Région de Bruxelles-Capitale.',
          nl: 'Ja, onze verwarmingsmonteurs zijn G1/G2 erkend en bezorgen het officiële reinigings- en verbrandingsattest vereist in het Brussels Hoofdstedelijk Gewest.',
          en: 'Yes, our certified heating engineers issue the official compliance and periodic maintenance certificate required in the Brussels-Capital Region.'
        }
      }
    ],
    testimonial: {
      author: 'Marc D.',
      neighborhood: 'Quartier Européen (1000)',
      text: {
        fr: 'Fuite importante sous l\'évier un dimanche matin. Technicien arrivé en 25 minutes, réparation propre et tarif annoncé à l\'avance. Très professionnel !',
        nl: 'Zware lek onder de gootsteen op zondagochtend. Binnen 25 minuten ter plaatse, nette herstelling en vooraf duidelijke prijs. Zeer professioneel!',
        en: 'Heavy leak under the kitchen sink on a Sunday morning. Technician arrived within 25 minutes, flawless repair and clear transparent quote!'
      },
      rating: 5,
      service: {
        fr: 'Recherche de fuite & Plomberie',
        nl: 'Lekdetectie & Loodgieterij',
        en: 'Leak Detection & Plumbing'
      }
    }
  },
  {
    slug: 'sint-pieters-leeuw',
    name: 'Sint-Pieters-Leeuw (1600)',
    postalCode: '1600',
    region: 'Flanders',
    regionLabel: {
      fr: 'Brabant Flamand (Périphérie Sud)',
      nl: 'Vlaams-Brabant (Zuidrand)',
      en: 'Flemish Brabant (South Rim)'
    },
    responseTime: '15 - 25 min',
    landmarks: {
      fr: ['Bergensesteenweg', 'Ruisbroek', 'Vlezenbeek', 'Zuun', 'Negenmanneke'],
      nl: ['Bergensesteenweg', 'Ruisbroek', 'Vlezenbeek', 'Zuun', 'Negenmanneke'],
      en: ['Bergensesteenweg', 'Ruisbroek', 'Vlezenbeek', 'Zuun', 'Negenmanneke']
    },
    headline: {
      fr: 'Plombier & Chauffagiste à Sint-Pieters-Leeuw (1600) - Siège API HEAT',
      nl: 'Loodgieter & Verwarmingsketel in Sint-Pieters-Leeuw (1600) - Hoofdzetel API HEAT',
      en: 'Plumber & Heating Engineer in Sint-Pieters-Leeuw (1600) - API HEAT HQ'
    },
    description: {
      fr: 'Intervention prioritaire et ultra-rapide à Sint-Pieters-Leeuw, Ruisbroek, Vlezenbeek et Zuun. Notre siège étant situé sur la Bergensesteenweg, nous garantissons les délais les plus courts de la région.',
      nl: 'Prioritaire en ultrasnelle interventie in Sint-Pieters-Leeuw, Ruisbroek, Vlezenbeek en Zuun. Met onze hoofdzetel aan de Bergensesteenweg garanderen wij de snelste responstijd.',
      en: 'Priority ultra-fast dispatch in Sint-Pieters-Leeuw, Ruisbroek, Vlezenbeek, and Zuun. Headquartered on Bergensesteenweg, we guarantee immediate response.'
    },
    localIntro: {
      fr: 'En tant qu\'entreprise locale basée au 24/8 Bergensesteenweg à 1600 Sint-Pieters-Leeuw, nos équipes sont au contact direct des habitants et copropriétés de la commune pour tout dépannage de plomberie, chaudière et égouts.',
      nl: 'Als lokaal bedrijf gevestigd aan de Bergensesteenweg 24/8 in 1600 Sint-Pieters-Leeuw, staan onze mobiele ploegen direct klaar voor particulieren en bedrijven in heel Leeuw.',
      en: 'As a local enterprise headquartered at Bergensesteenweg 24/8 in 1600 Sint-Pieters-Leeuw, our vans are ready to assist residents with plumbing, heating, and sewer emergencies.'
    },
    specialties: {
      fr: [
        'Intervention immédiate en 15-20 min depuis notre atelier central',
        'Installation et dépannage chaudières gaz toutes marques (Vaillant, Bulex, Viessmann)',
        'Débouchage de canalisation express avec hydrocureuse',
        'Pose et entretien d\'adoucisseurs d\'eau pour eau dure de Flandre'
      ],
      nl: [
        'Onmiddellijke interventie binnen 15-20 min vanuit ons centraal atelier',
        'Plaatsing en herstelling van cv-ketels (Vaillant, Bulex, Viessmann, Junkers)',
        'Spoedontstopping van riolen en toiletten met hogedrukwagen',
        'Plaatsing en onderhoud van ontkalkers tegen hard water in de Zennevallei'
      ],
      en: [
        'Immediate arrival within 15-20 mins straight from our central workshop',
        'Installation and emergency repair for Vaillant, Bulex, and Viessmann boilers',
        'Emergency sewer unblocking with high-pressure water jetting trucks',
        'Water softener installation tailored to Flemish Brabant water hardness'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Où se situe exactement votre siège à Sint-Pieters-Leeuw ?',
          nl: 'Waar bevindt zich jullie kantoor in Sint-Pieters-Leeuw?',
          en: 'Where is your head office located in Sint-Pieters-Leeuw?'
        },
        answer: {
          fr: 'Notre siège social et nos véhicules techniques sont situés au 24/8 Bergensesteenweg, 1600 Sint-Pieters-Leeuw.',
          nl: 'Onze maatschappelijke zetel en technische vloot bevinden zich aan de Bergensesteenweg 24/8, 1600 Sint-Pieters-Leeuw.',
          en: 'Our headquarters and technical fleet are located at Bergensesteenweg 24/8, 1600 Sint-Pieters-Leeuw.'
        }
      }
    ],
    testimonial: {
      author: 'Dirk V.',
      neighborhood: 'Vlezenbeek (1600)',
      text: {
        fr: 'Chaudière arrêtée en plein hiver. API HEAT étant à Sint-Pieters-Leeuw, ils sont arrivés en 20 min chrono. Panne réparée le jour même avec pièce d\'origine.',
        nl: 'Ketel uitgevallen midden in de winter. Aangezien API HEAT in Sint-Pieters-Leeuw zit, waren ze er binnen 20 minuten. Direct hersteld met wisselstuk.',
        en: 'Boiler shut down in freezing weather. API HEAT arrived in 20 minutes from Bergensesteenweg. Fixed on the spot with genuine spare parts.'
      },
      rating: 5,
      service: {
        fr: 'Dépannage Chaudière Gaz',
        nl: 'Herstelling Gasketel',
        en: 'Gas Boiler Repair'
      }
    }
  },
  {
    slug: 'ixelles',
    name: 'Ixelles / Elsene (1050)',
    postalCode: '1050',
    region: 'Brussels',
    regionLabel: {
      fr: 'Région de Bruxelles-Capitale',
      nl: 'Brussels Hoofdstedelijk Gewest',
      en: 'Brussels-Capital Region'
    },
    responseTime: '20 - 30 min',
    landmarks: {
      fr: ['Place Flagey', 'Châtelain', 'ULB / VUB', 'Avenue Louise', 'Porte de Namur', 'Matonge'],
      nl: ['Flageyplein', 'Kasteleinswijk', 'VUB / ULB', 'Louizalaan', 'Naamsepoort', 'Matongé'],
      en: ['Flagey Square', 'Chatelain', 'ULB / VUB University', 'Avenue Louise', 'Porte de Namur']
    },
    headline: {
      fr: 'Plombier Chauffagiste & Débouchage Urgent à Ixelles (1050)',
      nl: 'Loodgieter Verwarmingsmonteur & Spoedontstopping in Elsene (1050)',
      en: 'Emergency Plumber & Heating Engineer in Ixelles / Elsene (1050)'
    },
    description: {
      fr: 'Dépannage d\'urgence 24h/24 à Ixelles. Spécialistes des maisons de maître et appartements d\'Ixelles : recherche de fuite sans démolition, réparation chaudière gaz et débouchage WC/évier en 30 min.',
      nl: 'Dringende hersteldienst 24/7 in Elsene. Gespecialiseerd in herenhuizen en appartementen: lekdetectie zonder breekwerk, cv-ketel herstelling en ontstopping binnen 30 min.',
      en: '24/7 emergency plumbing and boiler repairs in Ixelles. Specialists in classic townhouses: non-destructive leak detection, heating maintenance, and drain unblocking in 30 mins.'
    },
    localIntro: {
      fr: 'Que vous soyez situé à Flagey, au Châtelain, près des universités ULB/VUB ou de la Porte de Namur, nos techniciens mobiles interviennent 7j/7 avec un matériel d\'hydrocurage et d\'inspection par caméra.',
      nl: 'Of u nu woont bij Flagey, het Kastelein, rond de ULB/VUB of de Naamsepoort: onze mobiele ploegen staan 7 op 7 paraat met camera-inspectie en hogedrukapparatuur.',
      en: 'Whether you reside in Flagey, Chatelain, near the universities, or Porte de Namur, our technicians are available 24/7 with camera inspection and hydro-jetting tools.'
    },
    specialties: {
      fr: [
        'Dépannage fuite d\'eau et sanitaire en 30 min à Flagey et Châtelain',
        'Entretien périodique chaudière gaz avec attestation de conformité',
        'Débouchage WC, douche et colonne d\'évacuation pour copropriétés',
        'Rénovation robinetterie et tuyauteries anciennes en plomb/cuivre'
      ],
      nl: [
        'Snelle lekherstelling binnen 30 min in Flagey en Kastelein',
        'Periodiek onderhoud gasketel met officieel reinigingsattest',
        'Ontstopping van toilet, douche en standleidingen in mede-eigendommen',
        'Vervanging van oude loden en koperen leidingen'
      ],
      en: [
        'Fast water leak repair within 30 mins in Flagey & Chatelain',
        'Periodic gas boiler maintenance with official compliance certificates',
        'Unblocking toilets, showers, and shared drain stacks for buildings',
        'Modernization and replacement of old lead and copper piping'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Pouvez-vous intervenir pour une canalisation bouchée dans un vieil immeuble d\'Ixelles ?',
          nl: 'Kunnen jullie een verstopte leiding ontstoppen in een oud herenhuis in Elsene?',
          en: 'Can you unblock old plumbing systems in classic Ixelles townhouses?'
        },
        answer: {
          fr: 'Oui, nous disposons de furets mécaniques de précision et de micro-caméras spécialement adaptés aux vieilles conduites délicates sans aucun risque d\'endommagement.',
          nl: 'Zeker, wij gebruiken speciaal aangepaste veermachines en endoscopische microcamera\'s om oude leidingen veilig te reinigen zonder beschadiging.',
          en: 'Yes, we use micro-cameras and precision mechanical snakes specifically suited to preserve vintage pipes while clearing stubborn blockages.'
        }
      }
    ],
    testimonial: {
      author: 'Sophie L.',
      neighborhood: 'Place Flagey (1050)',
      text: {
        fr: 'Canalisation générale bouchée au sous-sol. Équipe arrivée en 30 min avec la caméra et l\'hydrocureuse. Problème résolu en moins d\'une heure.',
        nl: 'Hoofdleiding verstopt in de kelder. Team ter plaatse binnen 30 min met camera en hogedruk. Opgelost in minder dan een uur.',
        en: 'Main sewer line backed up in our basement. The team arrived in 30 minutes with camera inspection and fixed everything cleanly.'
      },
      rating: 5,
      service: {
        fr: 'Débouchage haute pression',
        nl: 'Hogedruk ontstopping',
        en: 'High-pressure unblocking'
      }
    }
  },
  {
    slug: 'uccle',
    name: 'Uccle / Ukkel (1180)',
    postalCode: '1180',
    region: 'Brussels',
    regionLabel: {
      fr: 'Région de Bruxelles-Capitale (Sud)',
      nl: 'Brussels Hoofdstedelijk Gewest (Zuid)',
      en: 'Brussels-Capital Region (South)'
    },
    responseTime: '20 - 30 min',
    landmarks: {
      fr: ['Observatoire', 'Churchill', 'Fort Jaco', 'Saint-Job', 'Prince d\'Orange', 'Alsemberg'],
      nl: ['Sterrenwacht', 'Churchill', 'Fort Jaco', 'Sint-Job', 'Prins van Oranje', 'Alsemberg'],
      en: ['Observatory', 'Churchill', 'Fort Jaco', 'Saint-Job', 'Prince of Orange']
    },
    headline: {
      fr: 'Plombier Chauffagiste & Débouchage à Uccle (1180)',
      nl: 'Loodgieter CV-Ketel & Ontstopping in Ukkel (1180)',
      en: 'Emergency Plumbing, Heating & Drain Service in Uccle (1180)'
    },
    description: {
      fr: 'Services haut de gamme de plomberie et chauffage à Uccle (1180). Entretien de chaudières à condensation haute performance, recherche de fuite sans dégât et débouchage canalisation rapide.',
      nl: 'Hoogwaardige sanitair- en verwarmingsdiensten in Ukkel (1180). Onderhoud van condensatieketels, schadevrije lekdetectie en snelle rioolontstopping.',
      en: 'Premium plumbing and heating solutions in Uccle (1180). High-efficiency condensation boiler maintenance, non-destructive leak tracing, and fast unblocking.'
    },
    localIntro: {
      fr: 'Nos dépanneurs qualifiés interviennent quotidiennement dans tous les quartiers d\'Uccle (Fort Jaco, Churchill, Saint-Job, Prince d\'Orange et Dieweg) pour villas, maisons familiales et résidences.',
      nl: 'Onze ervaren technici zijn dagelijks actief in alle wijken van Ukkel (Fort Jaco, Churchill, Sint-Job, Prins van Oranje) voor villa\'s, woningen en residenties.',
      en: 'Our experienced heating technicians and master plumbers serve all Uccle neighborhoods (Fort Jaco, Churchill, Saint-Job, Prince of Orange) with high-end tools.'
    },
    specialties: {
      fr: [
        'Dépannage et réglage chaudières Viessmann, Vaillant, Bulex et Buderus',
        'Détection acoustique et thermographique de fuite dans les planchers chauffants',
        'Hydrocurage d\'égouts et fosses septiques à Uccle',
        'Installation d\'adoucisseurs d\'eau haute capacité pour grandes propriétés'
      ],
      nl: [
        'Onderhoud en herstelling van Viessmann, Vaillant, Bulex en Buderus ketels',
        'Thermografische en akoestische lekdetectie in vloerverwarming',
        'Hogedruk reiniging van rioleringen en toezichtsputten in Ukkel',
        'Plaatsing van hoogrendements-waterontharders voor grote woningen'
      ],
      en: [
        'Expert repair and maintenance for Viessmann, Vaillant, and Buderus systems',
        'Acoustic and thermal detection for underfloor heating leaks',
        'Hydro-jetting of private sewer lines and inspection chambers',
        'Installation of premium softeners tailored to large family residences'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Intervenez-vous pour les fuites de plancher chauffant à Uccle ?',
          nl: 'Kunnen jullie een lek in vloerverwarming opsporen in Ukkel?',
          en: 'Can you detect leaks in underfloor heating systems in Uccle?'
        },
        answer: {
          fr: 'Oui, nous utilisons des caméras thermiques infrarouges de pointe pour localiser la fuite exacte au centimètre près sans casser votre carrelage ou parquet.',
          nl: 'Ja, wij gebruiken infrarood thermografische camera\'s om het lek tot op de centimeter nauwkeurig te vinden zonder vloeren of tegels te beschadigen.',
          en: 'Yes, we utilize infrared thermal imaging cameras to pinpoint underfloor leaks with centimeter precision without damaging floors or tiles.'
        }
      }
    ],
    testimonial: {
      author: 'Guillaume M.',
      neighborhood: 'Fort Jaco (1180)',
      text: {
        fr: 'Remplacement de groupe de sécurité et entretien chaudière gaz. Travail soigné, techniciens ponctuels et très courtois. Facture conforme au devis.',
        nl: 'Vervanging van veiligheidsgroep en onderhoud gasketel. Zeer verzorgd werk, stipt en vriendelijk. Factuur klopte perfect met offerte.',
        en: 'Security valve replacement and complete annual boiler service. Punctual, meticulous craftsmanship, and honest pricing.'
      },
      rating: 5,
      service: {
        fr: 'Entretien Chaudière Gaz',
        nl: 'Onderhoud Gasketel',
        en: 'Boiler Service'
      }
    }
  },
  {
    slug: 'anderlecht',
    name: 'Anderlecht (1070)',
    postalCode: '1070',
    region: 'Brussels',
    regionLabel: {
      fr: 'Région de Bruxelles-Capitale (Ouest)',
      nl: 'Brussels Hoofdstedelijk Gewest (West)',
      en: 'Brussels-Capital Region (West)'
    },
    responseTime: '15 - 25 min',
    landmarks: {
      fr: ['Cureghem', 'Saint-Guidon', 'Erasme', 'Veeweyde', 'Boulevard Industriel', 'La Roue'],
      nl: ['Kuregem', 'Sint-Guido', 'Erasmus', 'Veeweide', 'Industrieboulevard', 'Het Rad'],
      en: ['Cureghem', 'Saint-Guidon', 'Erasmus Hospital', 'Veeweyde', 'Industrial Boulevard']
    },
    headline: {
      fr: 'Dépannage Plomberie Chauffage & Débouchage à Anderlecht (1070)',
      nl: 'Spoeddienst Loodgieterij & Ontstopping in Anderlecht (1070)',
      en: 'Emergency Plumbing, Heating & Drain Repair in Anderlecht (1070)'
    },
    description: {
      fr: 'Dépannage express en 20 minutes à Anderlecht (1070). Spécialiste chaudières, chauffe-eau, débouchage de canalisation et détection de fuite d\'eau 24/7.',
      nl: 'Express depannage binnen 20 minuten in Anderlecht (1070). Specialist in verwarmingsketels, boilers, rioolontstopping en lekdetectie 24/7.',
      en: 'Express emergency dispatch within 20 minutes in Anderlecht (1070). Boiler repair, water heaters, pipe unblocking, and leak detection 24/7.'
    },
    localIntro: {
      fr: 'Situé juste à la frontière de notre atelier de Sint-Pieters-Leeuw, Anderlecht bénéficie de délais d\'intervention record (15 à 20 min) pour toute urgence de plomberie ou panne de chaudière.',
      nl: 'Gelegen vlak naast onze werkplaats in Sint-Pieters-Leeuw, geniet Anderlecht van recordresponstijden (15 tot 20 min) voor elke sanitaire noodoproep.',
      en: 'Bordering our central base, Anderlecht enjoys record intervention times (15 to 20 mins) for plumbing leaks and heating breakdowns.'
    },
    specialties: {
      fr: [
        'Intervention en moins de 20 min dans tout Anderlecht',
        'Dépannage urgent chauffe-eau gaz et ballon électrique',
        'Hydrocurage canalisation bouchée et débouchage WC express',
        'Remplacement robinetterie et vannes d\'arrêt défectueuses'
      ],
      nl: [
        'Interventie binnen 20 minuten in heel Anderlecht',
        'Dringende herstelling gasboilers en elektrische warmwaterboilers',
        'Hogedrukontstopping van WC, afvoer en riool',
        'Vervanging van kranen en defecte hoofdkranen'
      ],
      en: [
        'Intervention under 20 mins across all of Anderlecht',
        'Emergency repairs for gas water heaters and electric tanks',
        'High-pressure drain jetting and express toilet clearing',
        'Replacement of shutoff valves and leaking faucets'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Quel est le temps d\'arrivée à Anderlecht (Cureghem, Saint-Guidon, Erasme) ?',
          nl: 'Wat is de aankomsttijd in Anderlecht?',
          en: 'What is your response time in Anderlecht?'
        },
        answer: {
          fr: 'En moyenne 15 à 20 minutes grâce à notre proximité immédiate via le Ring et la Chaussée de Mons (Bergensesteenweg).',
          nl: 'Gemiddeld 15 tot 20 minuten dankzij onze ligging vlakbij de Ring en de Bergensesteenweg.',
          en: 'Between 15 and 20 minutes on average due to our immediate proximity via the Ring and Chaussée de Mons.'
        }
      }
    ],
    testimonial: {
      author: 'Karim B.',
      neighborhood: 'Saint-Guidon (1070)',
      text: {
        fr: 'WC totalement refoulé le samedi soir. Arrivée du technicien en 20 min avec hydrocureuse portative. Débouché rapidement et sol laissé parfaitement propre.',
        nl: 'Toilet liep over op zaterdagavond. Binnen 20 minuten ter plaatse met hogedruk. Snel opgelost en alles netjes achtergelaten.',
        en: 'Toilet backing up on a Saturday night. Arrived in 20 mins with portable pressure jetter. Cleared fast and left spotless.'
      },
      rating: 5,
      service: {
        fr: 'Débouchage WC Urgent',
        nl: 'Spoedontstopping Toilet',
        en: 'Emergency Toilet Clearing'
      }
    }
  },
  {
    slug: 'waterloo',
    name: 'Waterloo (1410)',
    postalCode: '1410',
    region: 'Wallonia',
    regionLabel: {
      fr: 'Brabant Wallon',
      nl: 'Waals-Brabant',
      en: 'Walloon Brabant'
    },
    responseTime: '25 - 35 min',
    landmarks: {
      fr: ['Mont-Saint-Jean', 'Chenois', 'Joli-Bois', 'Faubourg', 'Braine-l\'Alleud frontière'],
      nl: ['Mont-Saint-Jean', 'Chenois', 'Joli-Bois', 'Faubourg', 'Grens Eigenbrakel'],
      en: ['Mont-Saint-Jean', 'Chenois', 'Joli-Bois', 'Faubourg', 'Braine-l\'Alleud Border']
    },
    headline: {
      fr: 'Plombier Chauffagiste & Débouchage à Waterloo (1410)',
      nl: 'Loodgieter Verwarming & Ontstopping in Waterloo (1410)',
      en: 'Plumbing, Heating & Sewer Unblocking in Waterloo (1410)'
    },
    description: {
      fr: 'Dépannage plomberie et chauffage dans le Brabant Wallon à Waterloo (1410) et Braine-l\'Alleud. Entretien chaudière mazout/gaz, recherche de fuite et débouchage canalisation 24/7.',
      nl: 'Sanitair en cv-onderhoud in Waals-Brabant te Waterloo (1410) en Eigenbrakel. Onderhoud gasketel/stookolie, lekdetectie en rioolontstopping 24/7.',
      en: 'Plumbing, oil and gas boiler services in Walloon Brabant: Waterloo (1410) and Braine-l\'Alleud. 24/7 leak detection and emergency drain jetting.'
    },
    localIntro: {
      fr: 'Nos dépanneurs parcourent Waterloo, Braine-l\'Alleud, Rhode-Saint-Genèse et Lasne pour sécuriser vos installations de chauffage et réparer vos fuites d\'eau sans attente.',
      nl: 'Onze technici doorkruisen Waterloo, Eigenbrakel, Sint-Genesius-Rode en Lasne om uw verwarmings- en sanitairinstallaties te herstellen.',
      en: 'Serving Waterloo, Braine-l\'Alleud, Rhode-Saint-Genèse, and Lasne with rapid response times for boiler maintenance and pipe leaks.'
    },
    specialties: {
      fr: [
        'Entretien légal chaudière gaz et mazout avec attestation SPW',
        'Dépannage d\'urgence fuites d\'eau et radiateurs froids',
        'Débouchage de fosses et canalisations extérieures',
        'Installation d\'adoucisseurs d\'eau pour protéger robinetterie et électroménagers'
      ],
      nl: [
        'Wettelijk ketelonderhoud gas en stookolie met officieel attest',
        'Dringende herstelling van waterlekken en koude radiatoren',
        'Ontstopping van rioleringen en toezichtsputten',
        'Plaatsing van waterontharders voor bescherming van leidingen'
      ],
      en: [
        'Official periodic maintenance for gas and oil boilers with compliance certs',
        'Emergency repair for pipe leaks and cold radiator circuits',
        'Hydro-jetting of external drain lines and soakaways',
        'Water softener installation protecting appliances from limescale'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Prenez-vous en charge les chaudières au mazout à Waterloo ?',
          nl: 'Onderhouden jullie ook stookolieketels in Waterloo?',
          en: 'Do you service heating oil boilers in Waterloo?'
        },
        answer: {
          fr: 'Oui, nous intervenons sur les chaudières au mazout et au gaz de toutes marques (Viessmann, Buderus, De Dietrich, Weishaupt) avec ramonage de conduit inclus.',
          nl: 'Ja, wij onderhouden zowel stookolie- als gasketels (Viessmann, Buderus, De Dietrich, Weishaupt) inclusief schoorsteenreiniging.',
          en: 'Yes, our certified heating technicians service both oil and gas burners across all major brands with chimney sweeping included.'
        }
      }
    ],
    testimonial: {
      author: 'François D.',
      neighborhood: 'Joli-Bois (1410)',
      text: {
        fr: 'Entretien chaudière au mazout impeccable. Chauffagiste très soigneux, attestation remise sur place. Je recommande les yeux fermés !',
        nl: 'Onberispelijk onderhoud van stookolieketel. Zeer zorgvuldige technicus, attest direct overhandigd. Aanrader!',
        en: 'Flawless oil boiler annual maintenance. Punctual, neat craftsmanship, certificate provided instantly. Highly recommended!'
      },
      rating: 5,
      service: {
        fr: 'Entretien Chaudière Mazout',
        nl: 'Onderhoud Stookolieketel',
        en: 'Oil Boiler Maintenance'
      }
    }
  }
];

export const CITIES_DATA: CityData[] = [
  ...CORE_CITIES_DATA,
  ...generateExtendedCities()
];

export function getCityBySlug(slug: string): CityData | undefined {
  return CITIES_DATA.find((c) => c.slug.toLowerCase() === slug.toLowerCase());
}
